import { useState } from 'react';
import { XCircle, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BankTransferStatusBadge } from './BankTransferStatusBadge';
import { ConfirmReceiptDialog } from './ConfirmReceiptDialog';
import { ExtendDeadlineDialog } from './ExtendDeadlineDialog';
import type { BankTransferQueueItem } from '../types/bankTransfer';
import { TERMINAL_BANK_TRANSFER_STATUSES } from '../types/bankTransfer';
import { formatDate } from '../utils/formatDate';

interface BankTransferDetailPanelProps {
  readonly item: BankTransferQueueItem;
  readonly onClose: () => void;
  readonly onActionSuccess: (message: string) => void;
}

export const BankTransferDetailPanel = ({
  item,
  onClose,
  onActionSuccess,
}: BankTransferDetailPanelProps) => {
  const { t } = useTranslation('pages');
  const [showConfirm, setShowConfirm] = useState(false);
  const [showExtend, setShowExtend] = useState(false);

  const isTerminal = TERMINAL_BANK_TRANSFER_STATUSES.includes(item.processStatus);
  const canConfirm =
    item.processStatus === 'AWAITING_TRANSFER' ||
    item.processStatus === 'DECLARED_AWAITING_SETTLEMENT';
  const canExtend =
    !isTerminal && item.transferDueAt != null && new Date(item.transferDueAt) > new Date();

  return (
    <>
      <aside
        className="fixed inset-y-0 right-0 w-full md:w-[480px] bg-white border-l border-gray-200 shadow-xl flex flex-col z-20"
        aria-label={t('bankTransfersDetailAriaLabel')}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{t('bankTransfersDetailHeading')}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label={t('bankTransfersClosePanel')}
          >
            <XCircle size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Refund warning */}
          {item.manualRefundObligationId != null && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>{t('bankTransfersDetailRefundWarning')}</span>
            </div>
          )}

          {/* Booking identity */}
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              {t('bankTransfersDetailSectionBooking')}
            </p>
            <div className="space-y-2 text-sm">
              <Row label={t('bankTransfersDetailBookingId')}>
                <span className="font-mono text-xs text-gray-700">{item.bookingId}</span>
              </Row>
              <Row label={t('bankTransfersColumnApartment')}>
                {item.apartmentName ?? '—'}
              </Row>
              <Row label={t('bankTransfersColumnGuest')}>
                <span>
                  {item.guestName ?? item.guestEmail}
                  {item.guestName && (
                    <span className="block text-xs text-gray-400">{item.guestEmail}</span>
                  )}
                </span>
              </Row>
              <Row label={t('bankTransfersColumnCheckIn')}>{formatDate(item.checkIn)}</Row>
              <Row label={t('bankTransfersColumnCheckOut')}>{formatDate(item.checkOut)}</Row>
              <Row label={t('bankTransfersColumnStatus')}>
                <BankTransferStatusBadge status={item.processStatus} />
              </Row>
            </div>
          </div>

          {/* Expected payment */}
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              {t('bankTransfersDetailSectionPayment')}
            </p>
            <div className="space-y-2 text-sm">
              <Row label={t('bankTransfersDetailExpectedStage')}>{item.expectedStage}</Row>
              <Row label={t('bankTransfersDetailExpectedAmount')}>
                {item.expectedAmount.toFixed(2)} {item.currency}
              </Row>
              <Row label={t('bankTransfersDetailPaymentReference')}>
                <span className="font-mono text-xs">{item.paymentReference ?? '—'}</span>
              </Row>
              {item.transferDueAt && (
                <Row label={t('bankTransfersDetailTransferDeadline')}>
                  {formatDate(item.transferDueAt.slice(0, 10))}
                </Row>
              )}
            </div>
          </div>

          {/* Declaration */}
          {item.declaration != null && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                {t('bankTransfersDetailSectionDeclaration')}
              </p>
              <div className="space-y-2 text-sm">
                <Row label={t('bankTransfersDetailDeclaredStage')}>{item.declaration.stage}</Row>
                {item.declaration.declaredAmount != null && (
                  <Row label={t('bankTransfersDetailDeclaredAmount')}>
                    {item.declaration.declaredAmount.toFixed(2)} {item.currency}
                  </Row>
                )}
                <Row label={t('bankTransfersDetailDeclaredDate')}>
                  {formatDate(item.declaration.declaredTransferDate)}
                </Row>
                <Row label={t('bankTransfersDetailDeclaredAt')}>
                  {formatDate(item.declaration.declaredAt.slice(0, 10))}
                </Row>
                {item.declaration.settlementConfirmationDueAt != null && (
                  <Row label={t('bankTransfersDetailSettlementDue')}>
                    {formatDate(item.declaration.settlementConfirmationDueAt.slice(0, 10))}
                  </Row>
                )}
                {item.declaration.guestNote && (
                  <div>
                    <p className="text-gray-500 mb-1">{t('bankTransfersDetailGuestNote')}</p>
                    <p className="text-gray-800 text-sm leading-relaxed whitespace-pre-wrap bg-gray-50 rounded p-2">
                      {item.declaration.guestNote}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-gray-100 flex flex-wrap gap-2">
          <button
            onClick={() => setShowConfirm(true)}
            disabled={!canConfirm}
            className="flex-1 text-sm font-medium px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t('bankTransfersConfirmReceiptButton')}
          </button>
          {canExtend && (
            <button
              onClick={() => setShowExtend(true)}
              className="flex-1 text-sm font-medium px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              {t('bankTransfersExtendDeadlineButton')}
            </button>
          )}
        </div>
      </aside>

      {showConfirm && (
        <ConfirmReceiptDialog
          item={item}
          onClose={() => setShowConfirm(false)}
          onSuccess={msg => {
            setShowConfirm(false);
            onActionSuccess(msg);
          }}
        />
      )}

      {showExtend && (
        <ExtendDeadlineDialog
          item={item}
          onClose={() => setShowExtend(false)}
          onSuccess={msg => {
            setShowExtend(false);
            onActionSuccess(msg);
          }}
        />
      )}
    </>
  );
};

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-2">
    <span className="text-gray-500 shrink-0">{label}</span>
    <span className="text-gray-900 text-right">{children}</span>
  </div>
);
